/**
 * Transfer wizard data loaders (staff, residents, applications, progress restore).
 */
import { useCallback, useEffect, useRef } from 'react'
import { showApiError, showValidationMessage } from '@/lib/utils/notifications'
import type { PropertyStaffRow } from '../types'
import * as transferApi from '../api/transferApi'
import {
  buildPendingTransferLogQueryParams,
  pendingTransferProcessConflictsWithFragment,
  TRANSFER_PROCESS_DISPLAY_LABELS,
} from '../utils/transferPropertyCompanyWizard'
import { computeDomainMode } from '../utils/transferProcessStaffUtils'
import type { TransferProgressFlagOverrides } from '../utils/transferProcessProgressPayload'
import type { TransferProcessWorkflowDeps } from './transferWorkflowTypes'

type ProgressApi = {
  saveTransferProgressInternal: (
    commitListOverride?: PropertyStaffRow[],
    flagOverrides?: TransferProgressFlagOverrides,
  ) => Promise<void>
  parseProgressData: (pd: Parameters<
    typeof import('../utils/transferParseProgressData').parseTransferProgressData
  >[0]) => void
}

export function useTransferProcessLoaders(
  { routing, dialogs, form, core, lookups }: TransferProcessWorkflowDeps,
  progress: ProgressApi,
) {
  const { fragment, entityId, statusParam, navigate, location } = routing
  const {
    process_id,
    process_id_notes,
    setProcess_id_notes,
    setAddEmployeeModalOpen,
    setAddEmployeeModalCtx,
  } = dialogs
  const { getValues, fkCompanyId } = form
  const { loadProperty } = lookups
  const {
    processing,
    setProcessing,
    entityLoadStatus,
    propertyData,
    allProperty,
    selectedTab,
    removestaffList,
    transferstaffList,
    addedstaffList,
    commitstaffList,
    residents,
    applications,
    selectAllResidentChecked,
    selectAllApplicationChecked,
    newPropertySave,
    setStaffList,
    setRemovestaffList,
    setAddedstaffList,
    setCommitstaffList,
    setDomains,
    setNewdomains,
    setResidents,
    setApplications,
    setPageOptions,
    setPageOptionsApplication,
    setResidentTablesRevision,
    setSelectedResidentList,
    setSelectedApplicationList,
    setSelectAllResidentChecked,
    setSelectAllApplicationChecked,
    setListedmanagementList,
    setProgressData,
    selectedResidentList,
    selectedApplicationList,
  } = core

  const urlParamsRef = useRef({
    page: 1,
    perPage: 15,
    sortBy: 'asc',
    orderBy: 'first_name',
    past_customer: '1',
  })
  const urlParamsAppRef = useRef({
    page: 1,
    perPage: 15,
    sortBy: 'asc',
    orderBy: 'applicant_id',
  })
  const residentsLoadSeqRef = useRef(0)
  const applicationsLoadSeqRef = useRef(0)

  const { saveTransferProgressInternal, parseProgressData } = progress

  const loadStaffs = useCallback(
    async (type?: 'aftercommit', flagOverrides?: TransferProgressFlagOverrides) => {
      if (!fragment || !entityId) return
      setProcessing(true)
      const requestData: Record<string, string | undefined> = {}
      try {
        if (fragment === 'merge_property' && type === 'aftercommit' && getValues('property_id')) {
          requestData.property_id = getValues('property_id')
        } else if (fragment === 'merge_property') {
          requestData.property_id = entityId
        } else if (fragment === 'transfer_company' && type === 'aftercommit') {
          requestData.company_id = getValues('fk_company_id')
        } else if (fragment === 'transfer_company') {
          requestData.company_id = entityId
        } else {
          requestData.property_id = entityId
        }
        const response = await transferApi.getPropertyStaff(requestData)
        if (type === 'aftercommit') {
          setCommitstaffList([...response])
          await saveTransferProgressInternal(response, flagOverrides)
        } else {
          setStaffList([...response])
          const seedRemove: PropertyStaffRow[] = []
          response.forEach((element) => {
            if (
              !removestaffList.filter((s) => s.role_id === element.role_id).length &&
              !transferstaffList.filter((s) => s.role_id === element.role_id).length &&
              !addedstaffList.filter((s) => s.role_id === element.role_id).length
            ) {
              seedRemove.push(element)
            }
          })
          // Angular appends (push); replacing would wipe remove-column draft on mid-flow re-fetch
          if (seedRemove.length) setRemovestaffList([...removestaffList, ...seedRemove])
          setDomains(computeDomainMode(response))
        }
      } catch (e) {
        showApiError(e)
      } finally {
        setProcessing(false)
      }
    },
    [
      fragment,
      entityId,
      getValues,
      removestaffList,
      transferstaffList,
      addedstaffList,
      saveTransferProgressInternal,
      setProcessing,
      setCommitstaffList,
      setStaffList,
      setRemovestaffList,
      setDomains,
    ],
  )

  const loadNewStaffs = useCallback(async () => {
    if (!fragment) return
    setProcessing(true)
    const requestData: Record<string, string | undefined> = {}
    try {
      if (fragment === 'transfer_property' && fkCompanyId) {
        requestData.company_id = fkCompanyId
      } else if (fragment === 'merge_property' && getValues('property_id')) {
        requestData.property_id = getValues('property_id')
      } else if (fragment === 'transfer_company') {
        requestData.company_id = fkCompanyId
      } else {
        requestData.property_id = entityId
      }
      const response = await transferApi.getPropertyStaff(requestData)
      setNewdomains(computeDomainMode(response))
    } catch (e) {
      showApiError(e)
    } finally {
      setProcessing(false)
    }
  }, [fragment, fkCompanyId, getValues, entityId, setProcessing, setNewdomains])

  const loadAddedStaffs = useCallback(async () => {
    if (!fragment) return
    setProcessing(true)
    const requestData: Record<string, string | undefined> = {}
    try {
      if (fragment === 'merge_property' && getValues('property_id')) {
        requestData.property_id = getValues('property_id')
      } else if (fragment === 'transfer_company' && propertyData) {
        requestData.company_id = fkCompanyId
      } else if (fragment === 'split_property') {
        requestData.property_id = newPropertySave
      } else {
        requestData.property_id = entityId
      }
      const response = await transferApi.getPropertyStaff(requestData)
      const nextAdd: PropertyStaffRow[] = []
      if (response?.length) {
        response.forEach((element) => {
          if (
            !commitstaffList.filter((s) => s.role_id === element.role_id).length &&
            !transferstaffList.filter((s) => s.role_id === element.role_id).length
          ) {
            nextAdd.push(element)
          }
        })
        setAddedstaffList(nextAdd)
      } else {
        setAddedstaffList([])
      }
    } catch (e) {
      showApiError(e)
    } finally {
      setProcessing(false)
    }
  }, [
    fragment,
    getValues,
    propertyData,
    fkCompanyId,
    newPropertySave,
    entityId,
    commitstaffList,
    transferstaffList,
    setProcessing,
    setAddedstaffList,
  ])

  const openAddEmployeeModal = useCallback(() => {
    if (!fragment || !propertyData) {
      showValidationMessage('Property data is not loaded yet.')
      return
    }
    const notesRaw = String(process_id_notes ?? '').trim()
    const transferNotesId = notesRaw || undefined

    if (fragment === 'transfer_company') {
      const companyId = String(fkCompanyId ?? '').trim()
      if (!companyId) {
        showValidationMessage('Select a company to transfer into before adding an employee.')
        return
      }
      setAddEmployeeModalCtx({ kind: 'company', companyId, transferNotesId })
      setAddEmployeeModalOpen(true)
      return
    }

    if (fragment === 'merge_property') {
      const mergePid = String(getValues('property_id') ?? '').trim()
      if (!mergePid) {
        showValidationMessage('Select the merge target property first, then add an employee.')
        return
      }
      const mergedProp = allProperty.find((p) => String(p.property_id ?? '') === mergePid)
      const companyId = String(
        mergedProp?.['fk_company_id'] ??
          mergedProp?.['company_id'] ??
          propertyData.fk_company_id ??
          propertyData.company_id ??
          '',
      ).trim()
      if (!companyId) {
        showValidationMessage('Could not resolve company for the selected property.')
        return
      }
      setAddEmployeeModalCtx({
        kind: 'propertyStaff',
        companyId,
        propertyId: mergePid,
        transferNotesId,
      })
      setAddEmployeeModalOpen(true)
      return
    }

    if (fragment === 'split_property') {
      const splitPid = String(newPropertySave ?? '').trim()
      if (!splitPid) {
        showValidationMessage('Save the new property first, then add employees to it.')
        return
      }
      const companyId = String(propertyData.fk_company_id ?? propertyData.company_id ?? '').trim()
      if (!companyId) {
        showValidationMessage('Could not resolve company for this transfer.')
        return
      }
      setAddEmployeeModalCtx({
        kind: 'propertyStaff',
        companyId,
        propertyId: splitPid,
        transferNotesId,
      })
      setAddEmployeeModalOpen(true)
      return
    }

    const propertyId = String(entityId ?? '').trim()
    if (!propertyId) {
      showValidationMessage('Missing property for this transfer.')
      return
    }
    const companyId = String(
      fragment === 'transfer_property' && String(fkCompanyId ?? '').trim()
        ? fkCompanyId
        : propertyData.fk_company_id ?? propertyData.company_id ?? '',
    ).trim()
    if (!companyId) {
      showValidationMessage('Select a company or wait for property details to finish loading.')
      return
    }
    setAddEmployeeModalCtx({
      kind: 'propertyStaff',
      companyId,
      propertyId,
      transferNotesId,
    })
    setAddEmployeeModalOpen(true)
  }, [
    fragment,
    propertyData,
    fkCompanyId,
    getValues,
    allProperty,
    newPropertySave,
    entityId,
    process_id_notes,
    setAddEmployeeModalCtx,
    setAddEmployeeModalOpen,
  ])

  const loadResidents = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!fragment || !entityId) return
      const seq = ++residentsLoadSeqRef.current
      if (!opts?.silent) setProcessing(true)
      try {
        const pg = urlParamsRef.current
        const params: Record<string, string | number | undefined> = {
          page: pg.page,
          perPage: pg.perPage,
          sortBy: pg.sortBy,
          orderBy: pg.orderBy,
          past_customer: pg.past_customer,
        }
        if (fragment === 'merge_property' && getValues('property_id')) {
          params.propertyId = String(getValues('property_id'))
        } else if (fragment === 'transfer_company' && String(fkCompanyId ?? '').trim()) {
          params.companyId = String(fkCompanyId)
        } else {
          params.propertyId = entityId
        }
        const res = await transferApi.getResidents(params)
        if (seq !== residentsLoadSeqRef.current) return
        setResidents(res.data)
        setPageOptions({
          current: res.current ?? 1,
          last: res.last ?? 1,
          total: res.total ?? 0,
        })
        setResidentTablesRevision((n) => n + 1)
        if (selectAllResidentChecked) {
          setSelectedResidentList(res.data.map((r) => r.application_id))
        }
      } catch (e) {
        showApiError(e)
      } finally {
        if (!opts?.silent) setProcessing(false)
      }
    },
    [
      fragment,
      entityId,
      getValues,
      fkCompanyId,
      selectAllResidentChecked,
      setProcessing,
      setResidents,
      setPageOptions,
      setResidentTablesRevision,
      setSelectedResidentList,
    ],
  )

  const loadApplications = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!fragment || !entityId) return
      const seq = ++applicationsLoadSeqRef.current
      if (!opts?.silent) setProcessing(true)
      try {
        const pg = urlParamsAppRef.current
        const params: Record<string, string | number | undefined> = {
          page: pg.page,
          perPage: pg.perPage,
          sortBy: pg.sortBy,
          orderBy: pg.orderBy,
        }
        if (fragment === 'merge_property' && getValues('property_id')) {
          params.property_id = getValues('property_id')
        } else if (fragment === 'transfer_company' && String(fkCompanyId ?? '').trim()) {
          params.company_id = String(fkCompanyId)
        } else {
          params.property_id = entityId
        }
        const res = await transferApi.getPendingApplications(params)
        if (seq !== applicationsLoadSeqRef.current) return
        setApplications(res.data)
        setPageOptionsApplication({
          current: res.current ?? 1,
          last: res.last ?? 1,
          total: res.total ?? 0,
        })
        setResidentTablesRevision((n) => n + 1)
        if (selectAllApplicationChecked) {
          setSelectedApplicationList(res.data.map((r) => r.application_id))
        }
      } catch (e) {
        showApiError(e)
      } finally {
        if (!opts?.silent) setProcessing(false)
      }
    },
    [
      fragment,
      entityId,
      getValues,
      fkCompanyId,
      selectAllApplicationChecked,
      setProcessing,
      setApplications,
      setPageOptionsApplication,
      setResidentTablesRevision,
      setSelectedApplicationList,
    ],
  )

  const loadListedManagement = useCallback(async () => {
    if (!entityId) return
    try {
      const res = await transferApi.getAlnPropertyManagers(entityId)
      setListedmanagementList(Array.isArray(res) ? (res as Record<string, unknown>[]) : [])
    } catch {
      setListedmanagementList([])
    }
  }, [entityId, setListedmanagementList])

  const loadTransferProgress = useCallback(async () => {
    if (!fragment || !entityId) return
    setProcessing(true)
    try {
      if (!statusParam) {
        const pendingParams = buildPendingTransferLogQueryParams(fragment, entityId)
        if (pendingParams) {
          const pendingRows = await transferApi.getTransferProcessLog(pendingParams)
          const pendingName = pendingRows[0]?.process_name
          if (pendingTransferProcessConflictsWithFragment(pendingName, fragment)) {
            showValidationMessage(
              `One pending ${TRANSFER_PROCESS_DISPLAY_LABELS[pendingName]} process exists for this ${fragment === 'transfer_company' ? 'company' : 'property'}. Resuming that process instead.`,
            )
            navigate(
              {
                pathname: location.pathname,
                search: location.search,
                hash: pendingName,
              },
              { replace: true },
            )
            return
          }
        }
      }

      const requestData: Record<string, string> = {
        process_name: fragment,
        status: statusParam || 'Pending',
      }
      if (process_id) requestData.process_id = process_id
      requestData[fragment === 'transfer_company' ? 'company_id' : 'property_id'] = entityId
      // Fresh entry queries only Pending (Angular parity) so a completed transfer stays invisible
      // and starting again begins a new transfer; the list resumes Completed via status/process_id.
      const response = await transferApi.getTransferProcessLog(requestData)
      if (response[0]?.json_content) {
        setProgressData(response[0].json_content)
        parseProgressData(response[0].json_content)
        await loadStaffs()
        if (response[0].id) setProcess_id_notes(String(response[0].id))
        await new Promise((r) => setTimeout(r, 110))
        await loadResidents()
        await loadApplications()
      } else {
        await loadStaffs()
        await loadResidents()
        await loadApplications()
        await loadListedManagement()
      }
    } catch (e) {
      showApiError(e)
    } finally {
      setProcessing(false)
    }
  }, [
    fragment,
    entityId,
    statusParam,
    navigate,
    location.pathname,
    location.search,
    process_id,
    parseProgressData,
    loadStaffs,
    loadResidents,
    loadApplications,
    loadListedManagement,
    setProcessing,
    setProgressData,
    setProcess_id_notes,
  ])

  useEffect(() => {
    if (entityLoadStatus !== 'ready' || selectedTab !== 'residentinfo' || !fragment || !entityId) {
      return
    }
    if (processing) return
    if (residents.length > 0 || applications.length > 0) return
    void loadResidents({ silent: true })
    void loadApplications({ silent: true })
  }, [
    selectedTab,
    entityLoadStatus,
    fragment,
    entityId,
    processing,
    residents.length,
    applications.length,
    loadResidents,
    loadApplications,
  ])

  useEffect(() => {
    const allOnPage =
      residents.length > 0 &&
      selectedResidentList.length === residents.length &&
      residents.every((row) => selectedResidentList.includes(row.application_id))
    if (selectAllResidentChecked && !allOnPage) {
      setSelectAllResidentChecked(false)
    }
  }, [residents, selectedResidentList, selectAllResidentChecked, setSelectAllResidentChecked])

  useEffect(() => {
    const allOnPage =
      applications.length > 0 &&
      selectedApplicationList.length === applications.length &&
      applications.every((row) => selectedApplicationList.includes(row.application_id))
    if (selectAllApplicationChecked && !allOnPage) {
      setSelectAllApplicationChecked(false)
    }
  }, [
    applications,
    selectedApplicationList,
    selectAllApplicationChecked,
    setSelectAllApplicationChecked,
  ])

  return {
    urlParamsRef,
    urlParamsAppRef,
    loadStaffs,
    loadNewStaffs,
    loadAddedStaffs,
    loadResidents,
    loadApplications,
    loadListedManagement,
    loadTransferProgress,
    openAddEmployeeModal,
    loadProperty,
  }
}
